<template>
  <div class="schedule-popup">
    <h2>Report Schedule</h2>
    <DxGridWithSearch :dataSource="schedulesForReport" :columns="columns" :toolbar="toolbar">
      <template v-slot:extraActions>
        <add-button @click="addSchedule" v-tooltip="'Click to add schedule.'" />
      </template>
      <template v-slot:actions-cell="{ data }">
        <div class="button-group d-flex">
          <icon-button
            v-tooltip.left="'Edit schedule.'"
            @click="editSchedule(data)"
            class="m-auto text-primary pointer p-0"
            :icon="'pen-alt'"
          />
          <icon-button
            @click="deleteSchedule(data)"
            v-tooltip.left="'Delete schedule.'"
            class="m-auto text-danger pointer p-0"
            :icon="'trash-alt'"
          />
        </div>
      </template>
    </DxGridWithSearch>

    <Modal :status="isAddEditPopupOpen" @close="toggleAddEditPopup">
      <div v-if="selectedSchedule">
        <h2>{{ selectedSchedule.id ? "Edit" : "Add" }} schedule</h2>
        <div class="row">
          <TimePicker
            label="Run Time"
            v-model="selectedSchedule.runTime"
            class="col-4"
            :validator="$v.selectedSchedule.runTime"
          />
          <PrinterSelectWithStatus
            class="col-8"
            label="Printer"
            v-model="selectedSchedule.printerId"
            :items="labPrinters"
            valueExpr="printerId"
            :validator="$v.selectedSchedule.printerId"
          />
        </div>
        <div class="row">
          <SelectInput
            class="col-4"
            label="Frequency"
            :items="frequencyOptions"
            v-model="selectedSchedule.runSchedule"
            :showClearButton="false"
          />
          <TagInput
            v-if="selectedSchedule.runSchedule === 2"
            class="col-8"
            label="Days"
            v-model="selectedSchedule.daysOfTheWeek"
            :items="daysOfTheWeekOptions"
            :validator="$v.selectedSchedule.daysOfTheWeek"
          />
          <SelectInput
            v-if="selectedSchedule.runSchedule === 5"
            class="col-4"
            label="Month"
            v-model="selectedSchedule.month"
            :items="monthOptions"
            :validator="$v.selectedSchedule.month"
          />
          <NumberInput
            v-if="[3, 5].includes(selectedSchedule.runSchedule)"
            class="col-4"
            label="Day of Month"
            v-model="selectedSchedule.dayOfMonth"
            :min="1"
            :max="dayOfMonthMax"
            :validator="$v.selectedSchedule.dayOfMonth"
          />
        </div>
        <fieldset>
          <div>
            <input
              type="radio"
              id="standard"
              v-model="selectedSchedule.timePeriodToDate"
              name="standard"
              :value="false"
            />
            <label class="font-weight-bold mx-1" for="All">{{ standardLabel }}</label>
          </div>
          <div>
            <input
              v-model="selectedSchedule.timePeriodToDate"
              type="radio"
              id="toDate"
              name="toDate"
              :value="true"
            />
            <label class="font-weight-bold mx-1" for="specific">{{ toDateLabel }}</label>
          </div>
        </fieldset>
        <h3>Params:</h3>
        <div class="row mx-auto">
          <div
            class="col-6"
            :key="param.Name"
            v-for="param in selectedSchedule.extraParameters.filter(
              e =>
                e.ParameterType !== 'DateTime' &&
                e.Name !== 'LabId' &&
                e.ParameterVisibility === 'Visible'
            )"
          >
            <component
              v-bind="createParamField(param)"
              :is="createParamField(param).is"
              :ref="param.Name"
              :disabled="param.Name === 'LabId'"
              @input="data => updateParam(data, param)"
            />
            <div v-if="param && param.Nullable" class="form-group align-self-start">
              <Checkbox
                v-tooltip="'Set to null'"
                type="checkbox"
                class="form-control-sm mx-2 mt-2"
                :data-param="param.Name"
                :id="param.Name + 'nullable'"
                :name="param.Name + 'nullable'"
                v-model="nullableParams[param.Name]"
              />
            </div>
            <div class="w-100" v-else>
              <small>This param is required.</small>
            </div>
          </div>
        </div>
        <SubmitCancelRow class="my-1" @submit="handleSubmit" @cancel="handleCancel" />
      </div>
    </Modal>
  </div>
</template>

<script>
import SelectInput from "./common/SelectInput.vue";
import NumberInput from "./common/NumberInput.vue";
import TextInput from "./common/TextInput.vue";
import DatePicker from "./common/DatePicker.vue";
import MultiSelectInput from "./common/TagInput.vue";
import Checkbox from "./common/Checkbox.vue";
import SubmitCancelRow from "./common/SubmitCancelRow.vue";
import { camelCase } from "lodash";
import { PrintersApi, ReportsApi } from "@/services";
import TimePicker from "./common/TimePicker.vue";
import PrinterSelectWithStatus from "./PrinterSelectWithStatus.vue";
import { sortBy } from "lodash";
import DxGridWithSearch from "./common/DxGridWithSearch.vue";
import moment from "moment";
import Modal from "./common/Modal.vue";
import IconButton from "./common/IconButton.vue";
import AddButton from "./common/AddButton.vue";
import TagInput from "./common/TagInput.vue";
import { handleErrors } from "@/modules/handleErrors";
import { mapState } from "vuex";
import { required } from "vuelidate/lib/validators";
import { helpers } from "vuelidate/lib/validators";
import { ReportScheduleFrequencyEnum } from "@/modules/enums";
import { formatDatetimeCell } from "@/modules/helpers";

export default {
  components: {
    SelectInput,
    NumberInput,
    DatePicker,
    TextInput,
    MultiSelectInput,
    Checkbox,
    SubmitCancelRow,
    TimePicker,
    PrinterSelectWithStatus,
    DxGridWithSearch,
    Modal,
    IconButton,
    AddButton,
    TagInput
  },
  props: ["selectedReport"],
  data() {
    return {
      selectedSchedule: null,
      defaultSchedule: {
        runSchedule: 1,
        runTime: null,
        isDeleted: false,
        reportId: this.selectedReport.id,
        extraParameters: [
          {
            name: "string",
            value: "string",
            dataType: "string"
          }
        ],
        daysOfWeekMask: 0,
        dayOfMonth: null,
        month: null,
        printerId: null,
        daysOfTheWeek: [],
        timePeriodToDate: false
      },
      frequencyOptions: [
        { id: 1, displayName: "Daily" },
        { id: 2, displayName: "Weekly" },
        { id: 3, displayName: "Monthly" },
        // { id: 4, displayName: "Quarterly" },
        { id: 5, displayName: "Annually" }
        // { id: 6, displayName: "Nth Weekday of Month" },
        // { id: 7, displayName: "Last Weekday of Month" }
      ],
      customReportParams: {},
      schedulesForReport: [],
      labPrinters: [],
      isAddEditPopupOpen: false,
      daysOfTheWeekOptions: [
        { id: 0, displayName: "Sunday" },
        { id: 1, displayName: "Monday" },
        { id: 2, displayName: "Tuesday" },
        { id: 3, displayName: "Wednesday" },
        { id: 4, displayName: "Thursday" },
        { id: 5, displayName: "Friday" },
        { id: 6, displayName: "Saturday" }
      ],
      monthOptions: [
        { id: 1, displayName: "January" },
        { id: 2, displayName: "February" },
        { id: 3, displayName: "March" },
        { id: 4, displayName: "April" },
        { id: 5, displayName: "May" },
        { id: 6, displayName: "June" },
        { id: 7, displayName: "July" },
        { id: 8, displayName: "August" },
        { id: 9, displayName: "September" },
        { id: 10, displayName: "October" },
        { id: 11, displayName: "November" },
        { id: 12, displayName: "December" }
      ]
    };
  },
  mounted() {
    this.getSchedulesForReport();
    PrintersApi.getLabPrinters().then(res => {
      this.labPrinters = sortBy(res, "displayName") || [];
    });
  },
  computed: {
    ...mapState({
      currentLab: state => state.currentLab,
      DefaultReportScheduleRunTime: state => state.labSettings.DefaultReportScheduleRunTime
    }),
    reportDefaultParams() {
      if (this.selectedReport?.reportParams) {
        return JSON.parse(this.selectedReport?.reportParams);
      }
      return [];
    },
    columns() {
      return [
        {
          dataField: "runTime",
          dataType: "time",
          calculateDisplayValue: data => {
            if (data.runTime) {
              return moment(data.runTime).format("h:mm a");
            }
            return "";
          }
        },
        {
          dataField: "runSchedule",
          caption: "Frequency",
          calculateDisplayValue: data => {
            let text = "Unknown";
            const frequencyType = this.frequencyOptions.find(e => e.id === data.runSchedule);
            if (frequencyType) {
              text = frequencyType.displayName;
            }
            let suffix = "th";
            const month = this.monthOptions.find(e => e.id === data.month);
            switch (data.runSchedule) {
              case 2:
                text += ` (${data.daysOfTheWeek
                  .map(e => this.daysOfTheWeekOptions[e].displayName)
                  .join(", ")})`;
                break;
              case 3:
                if (![11, 12, 13].includes(data.dayOfMonth)) {
                  const remainder = data.dayOfMonth % 10;
                  if (remainder === 1) {
                    suffix = "st";
                  } else if (remainder == 2) {
                    suffix = "nd";
                  } else if (remainder === 3) {
                    suffix = "rd";
                  }
                }
                text += ` (${data.dayOfMonth}${suffix} day of month)`;
                break;
              case 5:
                if (month) {
                  text += ` (${month.displayName} ${data.dayOfMonth})`;
                }
                break;
            }
            return text;
          }
        },
        {
          dataField: "printerId",
          caption: "Printer",
          lookup: {
            dataSource: this.labPrinters,
            displayExpr: "displayName",
            valueExpr: "printerId"
          }
        },
        { dataField: "scheduleCreatedBy", caption: "Created By" },
        {
          dataField: "scheduleCreatedOn",
          dataType: "datetime",
          caption: "Created On",
          calculateDisplayValue: data => {
            if (data.scheduleCreatedOn) {
              const formattedDate = formatDatetimeCell(data.scheduleCreatedOn);
              return moment(formattedDate).format("M/DD/YYYY h:mm a");
            }
            return "";
          }
        },
        { dataField: "scheduleModifiedBy", caption: "Updated By" },
        {
          dataField: "scheduleModifiedOn",
          dataType: "datetime",

          caption: "Updated On",
          calculateDisplayValue: data => {
            if (data.scheduleModifiedOn) {
              const formattedDate = formatDatetimeCell(data.scheduleModifiedOn);
              return moment(formattedDate).format("M/DD/YYYY h:mm a");
            }
            return "";
          }
        },
        {
          type: "buttons",
          caption: "Actions",
          cellTemplate: "actions-cell"
        }
      ];
    },
    toolbar() {
      return {
        items: [
          {
            location: "after",
            template: "extraActions"
          }
        ]
      };
    },
    dayOfMonthMax() {
      if (this.selectedSchedule.runSchedule === ReportScheduleFrequencyEnum.Annually) {
        // February - ignores leap years
        if (this.selectedSchedule.month === 2) {
          return 28;
        }
        // 30 days has September, April, June, and November
        if ([4, 6, 9, 11].includes(this.selectedSchedule.month)) {
          return 30;
        }
      }
      return 31;
    },
    standardLabel() {
      switch (this.selectedSchedule.runSchedule) {
        case ReportScheduleFrequencyEnum.Daily:
          return "Previous Day";
        case ReportScheduleFrequencyEnum.Weekly:
          return "Previous Week";
        case ReportScheduleFrequencyEnum.Monthly:
          return "Previous Month";
        case ReportScheduleFrequencyEnum.Annually:
          return "Previous Year";
      }
      return "Standard";
    },
    toDateLabel() {
      switch (this.selectedSchedule.runSchedule) {
        case ReportScheduleFrequencyEnum.Daily:
          return "Current Day";
        case ReportScheduleFrequencyEnum.Weekly:
          return "Week to Day";
        case ReportScheduleFrequencyEnum.Monthly:
          return "Month to Date";
        case ReportScheduleFrequencyEnum.Annually:
          return "Year to Date";
      }
      return "To Date";
    }
  },
  validations() {
    return {
      selectedSchedule: {
        runTime: { required },
        printerId: { required },
        daysOfTheWeek: {
          required: value =>
            this.selectedSchedule.runSchedule === ReportScheduleFrequencyEnum.Weekly
              ? helpers.req(value)
              : true
        },
        dayOfMonth: {
          required: value =>
            [ReportScheduleFrequencyEnum.Monthly, ReportScheduleFrequencyEnum.Annually].includes(
              this.selectedSchedule.runSchedule
            )
              ? helpers.req(value)
              : true
        },
        month: {
          required: value =>
            this.selectedSchedule.runSchedule === ReportScheduleFrequencyEnum.Annually
              ? helpers.req(value)
              : true
        }
      }
    };
  },
  methods: {
    async handleSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return window.notify("Please check your input & try again.", "error");
      }
      try {
        const payload = this.selectedSchedule;
        payload.extraParameters = this.reportDefaultParams.map(({ Name, ParameterType, Value }) => {
          if (Array.isArray(Value)) {
            Value = Value.join(",");
          }
          return { name: Name, dataType: ParameterType, value: Value };
        });
        await ReportsApi.createReportSchedule(payload);
        window.notify("Report run scheduled.");
        this.toggleAddEditPopup();
        this.selectedSchedule = null;
        this.getSchedulesForReport();
      } catch (error) {
        handleErrors(error);
      }
    },
    handleCancel() {
      this.toggleAddEditPopup();
      this.selectedSchedule = null;
    },
    createParamField(parameter) {
      const props = {
        name: parameter.Name,
        label: parameter.Prompt,
        id: parameter.Name,
        ref: parameter.Name
      };
      if (parameter.Nullable) {
        props.value = null;
        this.nullableParams[parameter.Name] = parameter.DefaultValuesIsNull;
        if (parameter.DefaultValuesIsNull) {
          props.disabled = true;
        }
      } else {
        props.required = true;
      }
      switch (parameter.ParameterType) {
        case "DateTime":
          props.is = "DatePicker";
          if (/time/i.test(parameter.Name)) {
            props.is = "Timepicker";
            props.type = "datetime";
            props.dateSerializationFormat = "yyyy-MM-ddTHH:mm:ssZ";
          }
          break;
        case "Integer":
          props.is = "TextInput";
          props.type = "number";
          props.class = "text-input-width";
          break;
        default:
          props.is = "TextInput";
          props.type = "text";
          props.class = "text-input-width";
          break;
      }

      if (parameter.MultiValue) {
        props.is = "MultiSelectInput";
        props.value = [];
        props.class = "text-input text-input-width";
        props.dropDownOptions = { resizeEnabled: true };
      }
      //If defaultValues is not null, then set the value to the default value.
      if (this.selectedSchedule?.id) {
        const scheduleParam = this.selectedSchedule.extraParameters.find(
          e => e.Name === parameter.Name
        );
        if (scheduleParam) {
          props.value = scheduleParam.Value;
        }
      } else if (!parameter.DefaultValuesIsNull && parameter.ParameterState === "HasValidValue") {
        if (parameter.DefaultValues && !parameter.MultiValue) {
          props.value = parameter.DefaultValues[0];
        } else {
          props.value = parameter.DefaultValues;
        }
      }

      if (parameter.ValidValuesIsNull === false) {
        props.valueExpr = "Value";
        props.displayExpr = "Label";
        props.dataSource = parameter.ValidValues;
        if (props.is === "TextInput" && parameter.ValidValues?.length > 0) {
          props.is = "SelectInput";
          props.value = parameter.ValidValues[0].Value;
          if (this.nullableParams[parameter.Name]) {
            this.nullableParams[parameter.Name] = false;
          }
        }
      }

      //If the parameter is a custom parameter that valid values are based on the current lab.
      let camelCaseParamName = camelCase(parameter.Name);
      //Absolute special case for the noteIDs which is a specific data source from the tags typecode.
      if (parameter.Prompt === "Note Ids") {
        camelCaseParamName = "noteIds";
      }
      if (this.customReportParams[camelCaseParamName]) {
        return { ...props, ...this.customReportParams[camelCaseParamName] };
      }
      return props;
    },
    editSchedule({ data }) {
      let params = this.reportDefaultParams.map(param => {
        if (param.ParameterType === "DateTime") {
          param = this.formatDateTimeParam(param, this.reportDefaultParams);
        } else {
          param.Value = null;
          const scheduleParam = data.extraParameters.find(e => e.name === param.Name);
          if (scheduleParam) {
            if (param.MultiValue) {
              param.Value = scheduleParam.value?.split(",") || [];
            } else {
              param.Value = scheduleParam.value;
            }
          }
        }
        return param;
      });
      this.selectedSchedule = { ...data, extraParameters: params };
      this.isAddEditPopupOpen = true;
    },
    getSchedulesForReport() {
      ReportsApi.getSchedulesForReport(this.selectedReport.id).then(res => {
        this.schedulesForReport = res;
        if (!res.length) {
          this.addSchedule();
        }
      });
    },
    toggleAddEditPopup() {
      this.isAddEditPopupOpen = !this.isAddEditPopupOpen;
    },
    async deleteSchedule({ data }) {
      try {
        const confirm = await window.confirm("Are you sure you want to delete this schedule?");
        if (!confirm) {
          return;
        }
        await ReportsApi.deleteReportSchedule(data.id);
        window.notify("Report schedule deleted.");
        this.getSchedulesForReport();
      } catch (error) {
        handleErrors(error);
      }
    },
    addSchedule() {
      this.selectedSchedule = this.defaultSchedule;
      if (this.DefaultReportScheduleRunTime) {
        this.selectedSchedule.runTime = this.DefaultReportScheduleRunTime;
      }
      this.selectedSchedule.extraParameters = this.reportDefaultParams.map(e => {
        e.Value = null;
        if (e.ParameterType === "DateTime") {
          e = this.formatDateTimeParam(e, this.reportDefaultParams);
        }
        if (e.Name === "LabId") {
          e.value = this.currentLab;
        }
        return e;
      });
      this.isAddEditPopupOpen = true;
    },
    formatDateTimeParam(param, paramList) {
      if (/^start/i.test(param.Name) || /^begin/i.test(param.Name)) {
        param.Value = "Start";
      } else if (/^end/i.test(param.Name)) {
        param.Value = "End";
      } else if (/2$/.test(param.Name)) {
        param.Value = "End";
      } else if (paramList.map(e => e.Name).includes(param.Name + 2)) {
        param.Value = "Start";
      }
      return param;
    },
    updateParam(value, paramData) {
      const index = this.selectedSchedule.extraParameters.map(e => e.Name).indexOf(paramData.Name);
      if (index > -1) {
        this.selectedSchedule.extraParameters[index].Value = value;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.schedule-popup {
  max-width: 75vw;
}
</style>
